import React, { useState, useEffect } from 'react';
import NewInvestmentFlow from './NewInvestmentFlow';

const Footer = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('legal');
  const [showMobileText, setShowMobileText] = useState(true);
  const [showMobileImages, setShowMobileImages] = useState(false);

  const handleExpand = () => {
    setIsExpanded(true);
    document.body.style.overflow = 'hidden';
  };

  const handleClose = () => {
    setIsExpanded(false);
    setIsImageModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const handleImageClick = () => {
    setIsImageModalOpen(true);
  };

  const handleImageModalClose = () => {
    setIsImageModalOpen(false);
  };

  // 모바일에서 텍스트 페이드 아웃 애니메이션
  useEffect(() => {
    if (!isExpanded) return;

    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      // 텍스트를 5초 후에 페이드 아웃 시작
      const textTimer = setTimeout(() => {
        setShowMobileText(false);
      }, 5000);

      // 텍스트가 사라진 후 이미지 표시 (5초 + 1초 페이드 아웃)
      const imageTimer = setTimeout(() => {
        setShowMobileImages(true);
      }, 6000);

      return () => {
        clearTimeout(textTimer);
        clearTimeout(imageTimer);
      };
    } else {
      // 데스크톱에서는 즉시 이미지 표시
      setShowMobileImages(true);
    }
  }, [isExpanded]);

  // 궤도 애니메이션 설정
  useEffect(() => {
    if (!isExpanded) return;

    const timer = setTimeout(() => {
      const wrapper = document.getElementById('animation-wrapper');
      if (!wrapper) return;

      const images = Array.from(wrapper.getElementsByClassName('floating-img'));
      const imageObjects = [];
      let animationFrameId;

      function setupOrbits() {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }

        imageObjects.length = 0;

        // 모바일 여부를 한 번만 확인
        const isMobile = window.innerWidth < 768;

        images.forEach((img, index) => {
          const baseSize = parseInt(img.dataset.size, 10);
          // 모바일에서는 이미지 크기를 85% (65% + 30% 증가), 데스크톱에서는 1.5배 크게
          const size = isMobile ? Math.round(baseSize * 0.85) : Math.round(baseSize * 1.5);
          img.style.width = size + 'px';
          img.style.height = size + 'px';

          const speed = (0.0002 + Math.random() * 0.0002) * (index % 2 === 0 ? 1 : -1);

          // 모바일에서 궤도를 화면 안쪽으로 조정
          const baseRadiusX = isMobile ? 60 : 200;   // 모바일 X 반지름 대폭 축소
          const baseRadiusY = isMobile ? 50 : 140;   // 모바일 Y 반지름 대폭 축소
          const randomRangeX = isMobile ? 30 : 120;  // 모바일 X 범위 축소
          const randomRangeY = isMobile ? 30 : 80;   // 모바일 Y 범위 축소

          imageObjects.push({
            el: img,
            size: size,
            radiusX: baseRadiusX + (Math.random() * randomRangeX),
            radiusY: baseRadiusY + (Math.random() * randomRangeY),
            angle: Math.random() * Math.PI * 2,
            speed: speed,
          });

          setTimeout(() => {
            // 모바일에서는 더 은은하게, 데스크톱에서는 기존 설정 유지
            const isMobile = window.innerWidth < 768;
            img.style.opacity = isMobile ? '0.4' : '0.6';
          }, index * 200);
        });

        animate();
      }

      let lastTime = 0;
      function animate(timestamp = 0) {
        const deltaTime = timestamp - lastTime;
        lastTime = timestamp;

        const centerX = wrapper.offsetWidth / 2;
        const centerY = wrapper.offsetHeight / 2;

        imageObjects.forEach(obj => {
          obj.angle += obj.speed * deltaTime;

          const x = centerX + obj.radiusX * Math.cos(obj.angle) - obj.size / 2;
          const y = centerY + obj.radiusY * Math.sin(obj.angle) - obj.size / 2;

          obj.el.style.transform = `translate(${x}px, ${y}px)`;
        });

        animationFrameId = requestAnimationFrame(animate);
      }

      setupOrbits();

      return () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
      };
    }, 1000);

    return () => clearTimeout(timer);
  }, [isExpanded]);


  return (
    <>
      <footer className="bg-gray-900 text-gray-300">
        <div className="container mx-auto px-6 py-12">
          {/* 상단 CTA 섹션 */}
          <div className="text-center mb-12 pb-8 border-b border-gray-800">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              이제, 더모아벤처스와 함께<br />
              현명한 투자를 시작할 시간입니다.
            </h2>
            <p className="text-gray-400 mb-6">
              벤처투자 절세 효과에 대해 더 자세히 알아보고 싶으신가요? 지금 바로 확인해보세요.
            </p>
            <button
              onClick={handleExpand}
              className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              더 알아보기
            </button>
          </div>

        {/* 회사 정보 섹션 */}
        <div className="text-left">
          <div className="mb-4">
            <h3 className="text-xl font-bold text-white mb-3">더모아벤처스 주식회사</h3>
          </div>

          <div className="flex flex-col md:flex-row md:gap-x-6 text-base space-y-2 md:space-y-0">
            <div className="flex">
              <span className="text-gray-500 whitespace-nowrap">사업자등록번호</span>
              <span className="ml-2">477-81-03977</span>
            </div>
            <div className="flex">
              <span className="text-gray-500 whitespace-nowrap">대표자</span>
              <span className="ml-2">정은혜</span>
            </div>
            <div className="flex flex-1">
              <span className="text-gray-500 whitespace-nowrap">주소</span>
              <span className="ml-2">서울특별시 서초구 사임당로 27 (서초동) 4층 비82호</span>
            </div>
          </div>
        </div>

        {/* 하단 카피라이트 */}
        <div className="mt-8 pt-8 border-t border-gray-800 text-center text-base text-gray-500">
          <p>© 2025 더모아벤처스 주식회사. All rights reserved.</p>
        </div>
      </div>
    </footer>

    {/* 배경 오버레이 */}
    {isExpanded && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={handleClose}
        style={{
          animation: 'fadeIn 0.3s ease-out'
        }}
      />
    )}

    {/* 플로팅 모달 */}
    {isExpanded && (
      <div
        className="fixed top-6 bottom-6 left-6 right-6 md:left-8 md:right-8 md:top-8 md:bottom-8 lg:left-12 lg:right-12 xl:left-16 xl:right-16 bg-white rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col"
        style={{
          animation: 'slideUpFloating 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards'
        }}
      >
        {/* 모달 헤더 */}
        <div className="flex items-center justify-end p-6 border-b border-gray-200">
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 모달 콘텐츠 */}
        <div className="overflow-y-auto p-6 flex-1" style={{ minHeight: 'calc(100vh - 12rem - 100px)' }}>
          {/* 탭 인터페이스 섹션 */}
          <div className="mb-8">
            <div>
              <div className="border-b border-gray-200 pb-6">
                <nav className="flex flex-wrap gap-3 md:gap-4 justify-center md:justify-start" aria-label="Tabs">
                  <button
                    className={`px-3 md:px-6 py-3 rounded-xl font-medium text-base md:text-lg transition-all duration-200 ${
                      activeTab === 'legal'
                        ? 'bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700 shadow-inner border border-blue-200'
                        : 'bg-gradient-to-br from-white to-gray-50 text-gray-600 shadow-lg border border-gray-200 hover:shadow-xl hover:from-gray-50 hover:to-gray-100 hover:text-gray-800'
                    }`}
                    onClick={() => setActiveTab('legal')}
                  >
                    법적 근거
                  </button>
                  <button
                    className={`px-3 md:px-6 py-3 rounded-xl font-medium text-base md:text-lg transition-all duration-200 ${
                      activeTab === 'eligibility'
                        ? 'bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700 shadow-inner border border-blue-200'
                        : 'bg-gradient-to-br from-white to-gray-50 text-gray-600 shadow-lg border border-gray-200 hover:shadow-xl hover:from-gray-50 hover:to-gray-100 hover:text-gray-800'
                    }`}
                    onClick={() => setActiveTab('eligibility')}
                  >
                    공제 대상자
                  </button>
                  <button
                    className={`px-3 md:px-6 py-3 rounded-xl font-medium text-base md:text-lg transition-all duration-200 ${
                      activeTab === 'process'
                        ? 'bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700 shadow-inner border border-blue-200'
                        : 'bg-gradient-to-br from-white to-gray-50 text-gray-600 shadow-lg border border-gray-200 hover:shadow-xl hover:from-gray-50 hover:to-gray-100 hover:text-gray-800'
                    }`}
                    onClick={() => setActiveTab('process')}
                  >
                    진행 절차
                  </button>
                </nav>
              </div>

              <div className="mt-6">
                {activeTab === 'eligibility' && (
                  <div className="bg-white p-6 md:p-8 rounded-2xl shadow-md border border-gray-100">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">소득공제 대상자</h3>
                    <p className="text-gray-600 mb-6">아래 요건을 충족하는 거주자라면 누구나 벤처투자 소득공제 혜택을 받을 수 있습니다.</p>
                    <ul className="space-y-4">
                      <li className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-6 w-6 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="ml-3 text-gray-700"><strong className="font-semibold text-gray-900">개인 투자자:</strong> 벤처기업 등에 직접 투자하거나 개인투자조합을 통해 간접 투자하는 개인.</p>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-6 w-6 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="ml-3 text-gray-700"><strong className="font-semibold text-gray-900">종합소득 신고 대상자:</strong> 근로소득, 사업소득 등 종합소득이 있는 거주자.</p>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-6 w-6 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="ml-3 text-gray-700"><strong className="font-semibold text-gray-900">3년 이상 투자 유지:</strong> 출자 또는 투자일로부터 3년 이상 경과 요건을 충족해야 합니다.</p>
                      </li>
                    </ul>
                    <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400">
                      <p className="text-sm text-yellow-800"><strong className="font-bold">참고:</strong> 타인의 출자지분이나 투자지분을 양수하는 방식으로 취득한 경우는 공제 대상에서 제외됩니다.</p>
                    </div>
                  </div>
                )}

                {activeTab === 'process' && (
                  <div className="bg-white p-6 md:p-8 rounded-2xl shadow-md border border-gray-100">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">소득공제 신청 절차</h3>
                    <ol className="relative border-l border-gray-200 space-y-8">
                      <li className="ml-6">
                        <span className="absolute flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full -left-4 ring-4 ring-white">
                          <span className="font-bold text-white">1</span>
                        </span>
                        <h4 className="font-bold text-lg text-gray-900">투자 및 출자</h4>
                        <p className="mt-1 text-gray-600">개인투자조합에 출자하거나 벤처기업에 직접 투자합니다.</p>
                      </li>
                      <li className="ml-6">
                        <span className="absolute flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full -left-4 ring-4 ring-white">
                          <span className="font-bold text-white">2</span>
                        </span>
                        <h4 className="font-bold text-lg text-gray-900">증빙 서류 발급</h4>
                        <p className="mt-1 text-gray-600">투자한 기업 또는 조합으로부터 '출자 등 소득공제 신청서'를 발급받습니다.</p>
                      </li>
                      <li className="ml-6">
                        <span className="absolute flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full -left-4 ring-4 ring-white">
                          <span className="font-bold text-white">3</span>
                        </span>
                        <h4 className="font-bold text-lg text-gray-900">종합소득세 신고</h4>
                        <p className="mt-1 text-gray-600">과세연도 다음 해 5월, 종합소득세 신고 시 발급받은 서류를 첨부하여 세무서에 제출합니다.</p>
                      </li>
                      <li className="ml-6">
                        <span className="absolute flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full -left-4 ring-4 ring-white">
                          <span className="font-bold text-white">4</span>
                        </span>
                        <h4 className="font-bold text-lg text-gray-900">소득공제 적용</h4>
                        <p className="mt-1 text-gray-600">신고 내용을 바탕으로 최종 세액이 결정되며, 소득공제가 적용됩니다.</p>
                      </li>
                    </ol>
                  </div>
                )}

                {activeTab === 'legal' && (
                  <div className="bg-white p-6 md:p-8 rounded-2xl shadow-md border border-gray-100">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">법적 근거</h3>
                    <p className="text-gray-600 mb-6">벤처투자 소득공제는 아래 법률에 근거하여 시행됩니다.</p>
                    <div className="space-y-4">
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <h4 className="font-semibold text-gray-800">조세특례제한법 제16조 (중소기업창업투자조합 출자 등에 대한 소득공제)</h4>
                        <p className="mt-2 text-sm text-gray-600">거주자가 중소기업창업투자조합, 신기술사업투자조합, 벤처투자조합 또는 전문투자조합에 출자 또는 투자한 경우, 그 금액의 일정 비율을 종합소득금액에서 공제하는 것을 규정합니다.</p>
                      </div>
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <h4 className="font-semibold text-gray-800">조세특례제한법 시행령 제14조</h4>
                        <p className="mt-2 text-sm text-gray-600">소득공제의 구체적인 공제율, 한도, 신청 절차 및 투자 대상 기업의 요건 등 세부 사항을 명시합니다.</p>
                      </div>
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <h4 className="font-semibold text-gray-800">벤처기업육성에 관한 특별조치법</h4>
                        <p className="mt-2 text-sm text-gray-600">벤처기업의 정의, 개인투자조합의 결성 및 운용 등 벤처투자 생태계 전반에 대한 내용을 다루며, 소득공제 제도의 기반이 됩니다.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 전문직 대상 애니메이션 - 전체 너비 활용 */}
            <div className="bg-gray-50 p-2 rounded-xl relative overflow-hidden min-h-[300px] md:min-h-[500px] mb-6">
                <div
                  id="animation-wrapper"
                  className={`absolute inset-0 overflow-hidden transition-opacity duration-1000 ${
                    showMobileImages ? 'opacity-100' : 'opacity-0 md:opacity-100'
                  }`}
                >
                  <img src="https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg" alt="의사" className="floating-img" data-size="110" />
                  <img src="https://images.pexels.com/photos/8112187/pexels-photo-8112187.jpeg" alt="변호사" className="floating-img" data-size="90" />
                  <img src="https://images.pexels.com/photos/4240498/pexels-photo-4240498.jpeg" alt="임원" className="floating-img" data-size="105" />
                  <img src="https://images.pexels.com/photos/3771097/pexels-photo-3771097.jpeg" alt="파일럿" className="floating-img" data-size="85" />
                  <img src="https://images.pexels.com/photos/5212317/pexels-photo-5212317.jpeg" alt="교수" className="floating-img" data-size="100" />
                  <img src="https://images.pexels.com/photos/5452293/pexels-photo-5452293.jpeg" alt="외과의사" className="floating-img" data-size="95" />
                </div>
                <div
                  className={`relative z-20 flex flex-col items-center justify-center h-full text-center px-2 py-8 md:px-4 md:py-8 transition-opacity duration-1000 ${
                    showMobileText ? 'opacity-100' : 'opacity-0 md:opacity-100'
                  }`}
                  style={{
                    animation: window.innerWidth < 768 ? 'slideUpMobile 0.8s ease-out' : 'none'
                  }}
                >
                  {/* 모바일: 카드 없이 직접 텍스트, 데스크톱: 카드 유지 */}
                  <div className="md:bg-white/90 md:backdrop-blur-sm md:rounded-2xl px-3 py-3 md:px-6 md:py-4 md:shadow-lg max-w-xs md:max-w-none">
                    <p className="text-lg md:text-2xl lg:text-3xl font-bold text-gray-800 leading-tight md:leading-relaxed">최고의 <span className="text-blue-600">전문가</span>들을 위한<br/>현명한 <span className="text-blue-600">자산 증식</span> 솔루션</p>
                  </div>
                  {/* 모바일에서 추가 텍스트 */}
                  <div className="mt-4 md:hidden">
                    <p className="text-sm text-gray-600">의사, 변호사, 교수, 파일럿 등</p>
                    <p className="text-sm text-gray-600">고소득 전문직을 위한 맞춤형 절세 전략</p>
                  </div>
                </div>
              </div>

            {/* 벤처 인증서 - 별도 섹션 */}
            <div className="bg-gray-50 p-4 rounded-xl mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-left md:text-center">벤처 인증서</h3>
                <div className="rounded-2xl overflow-hidden shadow-md bg-white">
                  <img
                    src="/images/venture-certificate.jpg"
                    alt="벤처기업 확인서"
                    className="w-full h-auto max-h-[400px] md:max-h-[450px] lg:max-h-[500px] object-contain cursor-pointer hover:scale-[1.02] transition-transform duration-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                      console.log('이미지 로드 실패:', e.target.src);
                    }}
                    onClick={handleImageClick}
                  />
                  <div style={{display: 'none'}} className="text-center text-gray-500 text-sm py-8">
                    벤처기업 확인서<br />
                    (이미지: /public/images/venture-certificate.jpeg)
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-2 text-center">클릭하면 크게 볼 수 있습니다</p>
              </div>

            {/* 투자/소득공제 핵심 주체 섹션 */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg border border-gray-100 mb-8">
              <NewInvestmentFlow />
            </div>

          {/* CTA 버튼 */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-xl text-white text-center">
            <h3 className="text-xl font-bold mb-2">지금 시작하면 2025년부터 혜택 적용</h3>
            <p className="mb-4 opacity-90">법적 근거가 확실한 절세 혜택을 놓치지 마세요</p>
            <button
              onClick={() => window.open('https://open.kakao.com/o/skdqupSh', '_blank')}
              className="bg-white text-blue-600 px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors"
            >
              무료 상담 신청하기
            </button>
          </div>
        </div>
      </div>
    )}

    {/* 이미지 모달 */}
    {isImageModalOpen && (
      <>
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-[60]"
          onClick={handleImageModalClose}
          style={{
            animation: 'fadeIn 0.3s ease-out'
          }}
        />
        <div
          className="fixed inset-4 z-[70] flex items-center justify-center"
          onClick={handleImageModalClose}
        >
          <div
            className="relative max-w-full max-h-full bg-white rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: 'imageModalZoom 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards'
            }}
          >
            {/* 닫기 버튼 */}
            <button
              onClick={handleImageModalClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full flex items-center justify-center shadow-lg transition-all"
            >
              <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* 이미지 */}
            <img
              src="/images/venture-certificate.jpg"
              alt="벤처기업 확인서 원본"
              className="max-w-full max-h-full object-contain"
              style={{ maxHeight: '95vh', maxWidth: '95vw' }}
            />
          </div>
        </div>
      </>
    )}

    {/* 애니메이션 스타일 */}
    <style jsx>{`
      .floating-img {
        position: absolute;
        border-radius: 9999px;
        object-fit: cover;
        filter: grayscale(60%) contrast(1);
        opacity: 0;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        border: 3px solid white;
        transition: opacity 1s ease-in-out;
        will-change: transform;
        top: 0;
        left: 0;
      }

      /* 모바일에서 흰색 테두리 제거하고 은은한 페이드 효과 */
      @media (max-width: 767px) {
        .floating-img {
          border: none;
          opacity: 0.5;
          filter: grayscale(70%) contrast(0.8);
        }
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes slideUpFloating {
        from {
          transform: translateY(100%) scale(0.95);
          opacity: 0;
        }
        to {
          transform: translateY(0) scale(1);
          opacity: 1;
        }
      }

      @keyframes imageModalZoom {
        from {
          transform: scale(0.8);
          opacity: 0;
        }
        to {
          transform: scale(1);
          opacity: 1;
        }
      }

      @keyframes slideUpMobile {
        from {
          transform: translateY(20px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
    `}</style>

    </>
  );
};

export default Footer;